import { useEffect, useRef, useState } from "react";
import { api, newKey, qs } from "../api.js";
import { fmt, t } from "../i18n.js";
import { MiniMap } from "../minimap.jsx";
import { go, isManager, useMe } from "../shared.jsx";
import { uploadFile } from "../uploader.js";
import { Empty, ErrorBox, Field, Loader, Modal, Pager, Progress, Tabs, useAction, useLoad, useToast } from "../ui.jsx";
import { TaskRow } from "./dashboard.jsx";

export function ProjectList() {
  const me = useMe();
  const [q, setQ] = useState("");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);
  const [creating, setCreating] = useState(false);
  useEffect(() => { const id = setTimeout(() => { setSearch(q); setPage(1); }, 350); return () => clearTimeout(id); }, [q]);
  const state = useLoad(() => api.get("/projects" + qs({ q: search, status, page, page_size: 20,
    include_archived: status === "archived" ? 1 : undefined })), [search, status, page]);
  return (
    <div className="page">
      <header className="page-head">
        <h1>{t("projects")}</h1>
        {isManager(me) && <button className="btn btn-primary" onClick={() => setCreating(true)}>مشروع جديد</button>}
      </header>
      <div className="filters">
        <input type="search" placeholder="بحث بالاسم أو الرقم المرجعي أو العميل" value={q} onChange={(e) => setQ(e.target.value)} aria-label="بحث" />
        <select value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }} aria-label="الحالة">
          <option value="">الحالية (بدون المؤرشفة)</option>
          {Object.keys(t("projectStatus")).map((s) => <option key={s} value={s}>{t("projectStatus." + s)}</option>)}
        </select>
      </div>
      <Loader state={state} isEmpty={(d) => !d.items.length} emptyView={<Empty title="لا توجد مشاريع">
        {isManager(me) ? "أنشئ مشروعًا وحدد موقعه وفرقه." : "ستظهر هنا المشاريع التي تعمل عليها."}</Empty>}>
        {(d) => (
          <>
            <div className="cards">{d.items.map((p) => (
              <a key={p.id} className="pcard" href={`#/projects/${p.id}`}>
                <div className="pcard-top"><span className="ref">{p.ref_code}</span><span className={"pill pill-p-" + p.status}>{t("projectStatus." + p.status)}</span></div>
                <h3>{p.name}</h3>
                <p className="muted">{[p.client_name, p.city].filter(Boolean).join(" — ") || "—"}</p>
                <Progress value={p.progress.percent} label={"إنجاز المشروع"} />
                {p.progress.mine?.task_count > 0 && <p className="small muted">مهامي: {p.progress.mine.approved_count} من {p.progress.mine.task_count} معتمدة</p>}
                <p className="small">{(p.teams || []).map((tm) => tm.name).join("، ") || "بدون فرق"}</p>
              </a>))}</div>
            <Pager page={d.page} total={d.total} size={d.page_size} onPage={setPage} />
          </>
        )}
      </Loader>
      {creating && <ProjectForm onClose={() => setCreating(false)} onSaved={(p) => go(`/projects/${p.id}`)} />}
    </div>
  );
}

function ProjectForm({ project, onClose, onSaved }) {
  const me = useMe();
  const editing = !!project;
  const [f, setF] = useState(() => ({
    ref_code: project?.ref_code || "", name: project?.name || "", client_name: project?.client_name || "",
    client_contact_name: project?.client_contact_name || "", client_contact_phone: project?.client_contact_phone || "",
    city: project?.city || "", branch: project?.branch || "", address: project?.address || "",
    latitude: project?.latitude ?? "", longitude: project?.longitude ?? "",
    description: project?.description || "", scope: project?.scope || "",
    start_date: project?.start_date || "", target_end_date: project?.target_end_date || "",
    status: project?.status || "planned", manager_id: project?.manager_id || "",
  }));
  const managers = useLoad(() => (me.role === "admin" ? api.get("/users?role=project_manager&status=active&page_size=100") : Promise.resolve({ items: [] })), []);
  const { busy, error, run } = useAction();
  const [geo, setGeo] = useState(null);
  const key = useRef(newKey());
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const locate = () => {
    if (!navigator.geolocation) { setGeo("المتصفح لا يدعم تحديد الموقع"); return; }
    setGeo("جارٍ تحديد موقعك…");
    navigator.geolocation.getCurrentPosition(
      (pos) => { setF((s) => ({ ...s, latitude: +pos.coords.latitude.toFixed(6), longitude: +pos.coords.longitude.toFixed(6) }));
        setGeo(`تم التحديد بدقة ${Math.round(pos.coords.accuracy)} متر تقريبًا`); },
      (err) => setGeo(err.code === 1 ? "رُفض إذن الموقع. يمكنك اختيار النقطة على الخريطة أو إدخال الإحداثيات." : "تعذر تحديد الموقع"),
      { enableHighAccuracy: true, timeout: 15000 });
  };
  const submit = (e) => {
    e.preventDefault();
    const body = Object.fromEntries(Object.entries(f).map(([k, v]) => [k, v === "" ? null : v]));
    body.latitude = body.latitude == null ? null : Number(body.latitude);
    body.longitude = body.longitude == null ? null : Number(body.longitude);
    if (me.role !== "admin") delete body.manager_id;
    const req = editing
      ? api.patch(`/projects/${project.id}`, { version: project.version, ...body }, { key: key.current })
      : api.post("/projects", { ...body, team_ids: [] }, { key: key.current });
    run(() => req, editing ? "تم حفظ المشروع" : "تم إنشاء المشروع").then(onSaved, () => {});
  };
  return (
    <Modal title={editing ? "تعديل المشروع" : "مشروع جديد"} onClose={onClose} wide>
      <form className="form-grid" onSubmit={submit}>
        <Field label="الرقم المرجعي"><input required value={f.ref_code} onChange={set("ref_code")} dir="ltr" /></Field>
        <Field label="اسم المشروع"><input required minLength={2} value={f.name} onChange={set("name")} /></Field>
        <Field label="العميل"><input value={f.client_name} onChange={set("client_name")} /></Field>
        <Field label="جهة الاتصال"><input value={f.client_contact_name} onChange={set("client_contact_name")} /></Field>
        <Field label="هاتف جهة الاتصال"><input type="tel" dir="ltr" value={f.client_contact_phone} onChange={set("client_contact_phone")} /></Field>
        <Field label="المدينة"><input value={f.city} onChange={set("city")} list="cities" /></Field>
        <datalist id="cities"><option value="مصراتة" /><option value="طرابلس" /><option value="بنغازي" /></datalist>
        <Field label="الفرع">
          <select value={f.branch} onChange={set("branch")}><option value="">—</option>
            {["مصراتة", "طرابلس", "بنغازي"].map((b) => <option key={b}>{b}</option>)}</select>
        </Field>
        <Field label="العنوان التفصيلي"><input value={f.address} onChange={set("address")} /></Field>
        <fieldset className="field span-2">
          <legend className="field-label">موقع المشروع</legend>
          <MiniMap lat={f.latitude} lng={f.longitude} onPick={(lat, lng) => setF((s) => ({ ...s, latitude: lat, longitude: lng }))} />
          <div className="coords">
            <label>خط العرض<input type="number" step="0.000001" min={-90} max={90} dir="ltr" value={f.latitude} onChange={set("latitude")} /></label>
            <label>خط الطول<input type="number" step="0.000001" min={-180} max={180} dir="ltr" value={f.longitude} onChange={set("longitude")} /></label>
            <button type="button" className="btn" onClick={locate}>استخدام موقعي الحالي</button>
            {(f.latitude !== "" || f.longitude !== "") && <button type="button" className="btn btn-sm" onClick={() => setF((s) => ({ ...s, latitude: "", longitude: "" }))}>مسح</button>}
          </div>
          {geo && <span className="field-hint">{geo}</span>}
        </fieldset>
        <Field label="تاريخ البداية"><input type="date" value={f.start_date} onChange={set("start_date")} /></Field>
        <Field label="النهاية المستهدفة"><input type="date" value={f.target_end_date} onChange={set("target_end_date")} /></Field>
        <Field label="الحالة">
          <select value={f.status} onChange={set("status")}>{Object.keys(t("projectStatus")).map((s) => <option key={s} value={s}>{t("projectStatus." + s)}</option>)}</select>
        </Field>
        {me.role === "admin" && (
          <Field label="مدير المشروع">
            <select value={f.manager_id} onChange={set("manager_id")}><option value="">—</option>
              {(managers.data?.items || []).map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}</select>
          </Field>
        )}
        <Field label="وصف المشروع"><textarea rows={3} value={f.description} onChange={set("description")} /></Field>
        <Field label="نطاق العمل"><textarea rows={3} value={f.scope} onChange={set("scope")} /></Field>
        <div className="span-2"><ErrorBox error={error} /></div>
        <div className="actions span-2">
          <button className="btn btn-primary" disabled={busy}>{busy ? "جارٍ الحفظ…" : editing ? "حفظ" : "إنشاء المشروع"}</button>
          <button type="button" className="btn" onClick={onClose}>{t("cancel")}</button>
        </div>
      </form>
    </Modal>
  );
}

export function ProjectDetail({ id }) {
  const state = useLoad(() => api.get(`/projects/${id}`), [id]);
  const [tab, setTab] = useState("overview");
  const [editing, setEditing] = useState(false);
  return (
    <div className="page">
      <a className="back" href="#/projects"> → المشاريع</a>
      <Loader state={state}>{(p) => (
        <>
          <header className="page-head">
            <div><div className="ref">{p.ref_code}</div><h1>{p.name}</h1>
              <div className="chips"><span className={"pill pill-p-" + p.status}>{t("projectStatus." + p.status)}</span>
                <span className="muted">{p.manager_name ? "مدير المشروع: " + p.manager_name : ""}</span></div></div>
            {p.my_access.manage && <button className="btn" onClick={() => setEditing(true)}>تعديل</button>}
          </header>
          <div className="grid-2">
            <section className="panel">
              <h2>نسبة إنجاز المشروع</h2>
              <Progress value={p.progress.percent} label="إنجاز المشروع" />
              <p className="small muted">محسوبة من أوزان المهام المعتمدة ({fmt.num(p.progress.approved_weight)} من {fmt.num(p.progress.total_weight)})، دون المهام الملغاة.</p>
              {p.progress.mine?.task_count > 0 && <>
                <h3>إنجاز مهامي في هذا المشروع</h3>
                <Progress value={p.progress.mine.percent} label="إنجاز مهامي" />
              </>}
            </section>
            <section className="panel">
              <h2>الموقع</h2>
              <p>{[p.city, p.address].filter(Boolean).join(" — ") || "—"}</p>
              {p.maps ? <>
                <MiniMap lat={p.latitude} lng={p.longitude} height={200} />
                <div className="actions">
                  <a className="btn btn-primary" href={p.maps.google} target="_blank" rel="noopener">الاتجاهات</a>
                  <a className="btn" href={p.maps.apple} target="_blank" rel="noopener">خرائط Apple</a>
                  <span className="muted small" dir="ltr">{p.latitude}, {p.longitude}</span>
                </div></> : <p className="muted">لم يُحدد الموقع بعد.</p>}
            </section>
          </div>
          <Tabs value={tab} onChange={setTab} tabs={[["overview", "التفاصيل"], ["tasks", "المهام"], ["teams", "الفرق والأعضاء"],
            ["phases", "المراحل"], ["files", "الملفات"]]} />
          {tab === "overview" && (
            <section className="panel">
              <dl className="infos">
                <div className="info"><dt>العميل</dt><dd>{p.client_name || "—"}</dd></div>
                <div className="info"><dt>جهة الاتصال</dt><dd>{p.client_contact_name || "—"} {p.client_contact_phone && <a dir="ltr" href={`tel:${p.client_contact_phone}`}>{p.client_contact_phone}</a>}</dd></div>
                <div className="info"><dt>الفرع</dt><dd>{p.branch || "—"}</dd></div>
                <div className="info"><dt>المدة</dt><dd>{p.start_date || "—"} ← {p.target_end_date || "—"}</dd></div>
              </dl>
              {p.description && <><h3>الوصف</h3><p className="prose">{p.description}</p></>}
              {p.scope && <><h3>نطاق العمل</h3><p className="prose">{p.scope}</p></>}
            </section>
          )}
          {tab === "tasks" && <ProjectTasks project={p} />}
          {tab === "teams" && <ProjectTeams project={p} onChanged={state.setData} />}
          {tab === "phases" && <Phases project={p} onChanged={state.reload} />}
          {tab === "files" && <ProjectFiles project={p} />}
          {editing && <ProjectForm project={p} onClose={() => setEditing(false)} onSaved={(d) => { state.setData(d); setEditing(false); }} />}
        </>
      )}</Loader>
    </div>
  );
}

function ProjectTasks({ project }) {
  const [page, setPage] = useState(1);
  const state = useLoad(() => api.get(`/tasks?project_id=${project.id}&page=${page}&page_size=20`), [project.id, page]);
  return (
    <section className="panel">
      {project.my_access.assign && <a className="btn btn-primary" href={`#/tasks?project_id=${project.id}`}>إدارة مهام المشروع</a>}
      <Loader state={state} isEmpty={(d) => !d.items.length} emptyView={<p className="muted">لا توجد مهام ظاهرة لك في هذا المشروع.</p>}>
        {(d) => <><div className="list">{d.items.map((x) => <TaskRow key={x.id} task={x} extra={` — ${x.assignee_name}`} />)}</div>
          <Pager page={d.page} total={d.total} size={d.page_size} onPage={setPage} /></>}
      </Loader>
    </section>
  );
}

function ProjectTeams({ project, onChanged }) {
  const teams = useLoad(() => (project.my_access.manage ? api.get("/teams?page_size=100") : Promise.resolve({ items: [] })), []);
  const users = useLoad(() => (project.my_access.manage ? api.get("/users?status=active&page_size=100") : Promise.resolve({ items: [] })), []);
  const [sel, setSel] = useState(project.teams.map((x) => x.id));
  const [member, setMember] = useState({ user_id: "", project_role: "team_lead", can_assign: true, can_view_evaluations: false });
  const { busy, error, run } = useAction();
  return (
    <div className="grid-2">
      <section className="panel">
        <h2>الفرق المشاركة<span className="count">{project.teams.length}</span></h2>
        {project.my_access.manage ? (
          <>
            <div className="checks">{(teams.data?.items || []).filter((x) => x.active).map((tm) => (
              <label className="check" key={tm.id}><input type="checkbox" checked={sel.includes(tm.id)}
                onChange={(e) => setSel(e.target.checked ? [...sel, tm.id] : sel.filter((x) => x !== tm.id))} />
                {tm.name}{tm.specialty ? ` — ${tm.specialty}` : ""}</label>))}</div>
            <ErrorBox error={error} />
            <button className="btn btn-primary" disabled={busy} onClick={() => run(() => api.put(`/projects/${project.id}/teams`, { team_ids: sel }), "تم تحديث الفرق").then(onChanged, () => {})}>حفظ الفرق</button>
          </>
        ) : <ul>{project.teams.map((tm) => <li key={tm.id}>{tm.name}{tm.leader_name ? ` — القائد: ${tm.leader_name}` : ""}</li>)}</ul>}
      </section>
      {project.my_access.manage && (
        <section className="panel">
          <h2>صلاحيات أعضاء المشروع</h2>
          <p className="muted small">امنح قائد الفريق صلاحية توزيع المهام داخل فريقه، أو أضف مديرًا مشاركًا يعتمد المهام.</p>
          <table className="table"><thead><tr><th>العضو</th><th>الدور</th><th>توزيع المهام</th><th>رؤية التقييمات</th><th /></tr></thead>
            <tbody>{(project.members || []).map((m) => (
              <tr key={m.user_id}><td>{m.name}</td><td>{{ manager: "مدير مشارك", team_lead: "قائد فريق", member: "عضو" }[m.project_role]}</td>
                <td>{m.can_assign ? "نعم" : "لا"}</td><td>{m.can_view_evaluations ? "نعم" : "لا"}</td>
                <td><button className="btn btn-sm" onClick={() => run(() => api.del(`/projects/${project.id}/members/${m.user_id}`), "أُزيل العضو").then(onChanged, () => {})}>إزالة</button></td></tr>))}</tbody></table>
          <div className="form-grid">
            <Field label="الموظف"><select value={member.user_id} onChange={(e) => setMember({ ...member, user_id: e.target.value })}>
              <option value="">اختر</option>{(users.data?.items || []).map((u) => <option key={u.id} value={u.id}>{u.name} — {t("role." + u.role)}</option>)}</select></Field>
            <Field label="الدور في المشروع"><select value={member.project_role} onChange={(e) => setMember({ ...member, project_role: e.target.value })}>
              <option value="team_lead">قائد فريق</option><option value="manager">مدير مشارك</option><option value="member">عضو</option></select></Field>
            <label className="check"><input type="checkbox" checked={member.can_assign} onChange={(e) => setMember({ ...member, can_assign: e.target.checked })} />توزيع المهام داخل فريقه</label>
            <label className="check"><input type="checkbox" checked={member.can_view_evaluations} onChange={(e) => setMember({ ...member, can_view_evaluations: e.target.checked })} />رؤية تقييمات أعضاء المشروع</label>
          </div>
          <button className="btn" disabled={!member.user_id || busy} onClick={() => {
            const { user_id, ...body } = member;
            run(() => api.put(`/projects/${project.id}/members/${user_id}`, body), "تم حفظ الصلاحيات").then(onChanged, () => {});
          }}>حفظ العضو</button>
        </section>
      )}
    </div>
  );
}

function Phases({ project, onChanged }) {
  const [name, setName] = useState("");
  const { busy, run } = useAction();
  return (
    <section className="panel">
      <ol className="phases">{project.phases.map((ph) => (
        <li key={ph.id}><span>{ph.name}</span><small className="muted">{ph.start_date || ""} {ph.end_date ? "← " + ph.end_date : ""}</small>
          {project.my_access.manage && <button className="icon-btn" aria-label="حذف المرحلة" onClick={() => run(() => api.del(`/phases/${ph.id}`), "حُذفت المرحلة").then(onChanged, () => {})}>✕</button>}
        </li>))}</ol>
      {project.phases.length === 0 && <p className="muted">لا توجد مراحل.</p>}
      {project.my_access.manage && (
        <div className="inline-form">
          <input placeholder="اسم المرحلة (مثال: التمديدات)" value={name} onChange={(e) => setName(e.target.value)} aria-label="اسم المرحلة" />
          <button className="btn" disabled={busy || !name.trim()} onClick={() => run(() => api.post(`/projects/${project.id}/phases`, { name, sort_order: project.phases.length }), "أُضيفت المرحلة").then(() => { setName(""); onChanged(); }, () => {})}>إضافة مرحلة</button>
        </div>
      )}
    </section>
  );
}

function ProjectFiles({ project }) {
  const state = useLoad(() => api.get(`/projects/${project.id}/files`), [project.id]);
  const [progress, setProgress] = useState(null);
  const toast = useToast();
  const { run } = useAction();
  const upload = async (files) => {
    for (const f of files) {
      try {
        await uploadFile(f, { project_id: project.id, category: "project_file" }, (p) => setProgress({ name: f.name, ...p }));
        toast("رُفع " + f.name);
      } catch (e) { toast(`${f.name}: ${e.message}`, "error"); }
    }
    setProgress(null);
    state.reload();
  };
  return (
    <section className="panel">
      {project.my_access.manage && (
        <label className="btn btn-primary file-btn">رفع ملفات عامة للمشروع
          <input type="file" multiple hidden onChange={(e) => { upload([...e.target.files]); e.target.value = ""; }} /></label>
      )}
      {progress && <p className="muted">{progress.name}: {Math.round((100 * progress.sent) / progress.total)}%</p>}
      <Loader state={state} isEmpty={(d) => !d.items.length} emptyView={<p className="muted">لا توجد ملفات عامة.</p>}>
        {(d) => <ul className="plain">{d.items.map((a) => (
          <li key={a.id}><button className="link" onClick={() => run(() => api.download(`/attachments/${a.id}/download`, a.original_name)).catch(() => {})}>{a.original_name}</button>
            <small className="muted"> — {a.uploaded_by_name} — {fmt.dt(a.created_at)} — {fmt.size(a.size_bytes)}</small></li>))}</ul>}
      </Loader>
    </section>
  );
}
